import { Box, Chip, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { TextField } from 'components/core/textfield'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { Check, X, Minus } from 'lucide-react'
import type { TestTokenStepResult } from 'pbts/c1/api/workload_federation/v1/workload_federation'
import {
    WorkloadFederationServiceTestTokenRequest,
} from 'pbts/c1/api/workload_federation/v1/workload_federation'
import { FC, type ReactElement, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { testTokenApi } from 'redux_toolkit/workloadFederation'

type TestTokenPanelProps = ClosableProps & {
    servicePrincipalId: string
    clientId: string
    trustDisplayName: string
}

const StepResultRow: FC<{ result: TestTokenStepResult | undefined }> = ({ result }) => {
    const intl = useIntl()
    if (!result) return null

    let icon: ReactElement
    let color: string
    if (result.skipped) {
        icon = <C1Icon icon={Minus} sx={{ fontSize: 18, color: 'text.disabled' }} />
        color = 'text.disabled'
    } else if (result.passed) {
        icon = <C1Icon icon={Check} sx={{ fontSize: 18, color: 'success.main' }} />
        color = 'success.main'
    } else {
        icon = <C1Icon icon={X} sx={{ fontSize: 18, color: 'error.main' }} />
        color = 'error.main'
    }

    return <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.5 }}>
        <Box sx={{ pt: 0.25 }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500} color={color}>
                {result.stepName}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
                {result.detail}
            </Typography>
            {result.expected && result.actual && result.expected !== result.actual && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip label={intl.formatMessage({ id: 'wf_test_step_expected', defaultMessage: 'Expected: {value}' }, { value: result.expected })} size="small" variant="outlined" />
                    <Chip label={intl.formatMessage({ id: 'wf_test_step_actual', defaultMessage: 'Actual: {value}' }, { value: result.actual })} size="small" color="error" variant="outlined" />
                </Stack>
            )}
        </Box>
    </Stack>
}

export const TestTokenPanel: FC<TestTokenPanelProps> = ({ close, servicePrincipalId, clientId, trustDisplayName }) => {
    const intl = useIntl()
    const { mutate: testToken, inProgress, error, data: result } = testTokenApi()
    const [jwt, setJwt] = useState('')

    const handleTest = useCallback(async (): Promise<void> => {
        if (inProgress || !jwt.trim()) return
        await testToken(WorkloadFederationServiceTestTokenRequest.create({
            servicePrincipalId,
            clientId,
            subjectToken: jwt.trim(),
        }))
    }, [testToken, servicePrincipalId, clientId, jwt, inProgress])

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <FormattedMessage
                id="wf_test_token_title"
                defaultMessage="Test trust: {name}"
                values={{ name: trustDisplayName }}
            />
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <Stack spacing={3}>
                <APIError err={error} />
                <Box>
                    <Typography variant="body2" color="text.secondary" pb={1}>
                        <FormattedMessage
                            id="wf_test_token_jwt_label"
                            defaultMessage="Paste a JWT from your CI job, or a JSON claims object"
                        />
                    </Typography>
                    <TextField
                        value={jwt}
                        onChange={(e): void => setJwt(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={'eyJhbGciOiJSUzI1NiIs...\n\nor\n\n{"iss": "https://...", "sub": "repo:org/repo:ref:refs/heads/main"}'}
                        sx={{ fontFamily: 'monospace' }}
                    />
                </Box>
                <C1Button
                    onClick={handleTest}
                    disabled={inProgress || !jwt.trim()}
                    variant="contained"
                >
                    <FormattedMessage id="wf_test_token_run" defaultMessage="Run test" />
                </C1Button>

                {result && (
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6">
                                <FormattedMessage id="wf_test_token_results" defaultMessage="Results" />
                            </Typography>
                            <Chip
                                label={result.overallResult
                                    ? intl.formatMessage({ id: 'wf_test_token_passed', defaultMessage: 'Passed' })
                                    : intl.formatMessage({ id: 'wf_test_token_failed', defaultMessage: 'Failed' })
                                }
                                color={result.overallResult ? 'success' : 'error'}
                                size="small"
                            />
                        </Stack>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <StepResultRow result={result.jwtDecode} />
                            <StepResultRow result={result.issuerMatch} />
                            <StepResultRow result={result.signatureValidation} />
                            <StepResultRow result={result.audienceValidation} />
                            <StepResultRow result={result.tokenFreshness} />
                            <StepResultRow result={result.celEvaluation} />
                            <StepResultRow result={result.cidrCheck} />
                        </Box>

                        {result.decodedClaimsJson && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" pb={1}>
                                    <FormattedMessage
                                        id="wf_test_token_decoded_claims"
                                        defaultMessage="Decoded claims"
                                    />
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        backgroundColor: 'grey.50',
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        maxHeight: 300,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {result.decodedClaimsJson}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
        </C1DrawerInner.Content>
        <C1DrawerInner.Footer>
            <C1Button onClick={close} variant="outlined">
                <FormattedMessage id="wf_test_token_done" defaultMessage="Done" />
            </C1Button>
        </C1DrawerInner.Footer>
    </C1DrawerInner>
}
